import { Injectable } from "@angular/core";
import { poe1, poe2 } from "../../../game";
import { Comparator, JewelleryType, JewelType, Rarity, YesNoAny } from "../../models/enums";
import { EquipmentType } from "../../models/equipment-type.enum";
import { Filter, CustomRule, AdvancedConditions, JewelleryFilter, JewelFilter } from "../../models/filter-models";
import { FormatRuleOptions, RuleGeneratorService } from "./rule-generator.service";
import { GenerationHelperService } from "./generation-helper.service";
import { ItemDataService } from "../item-data.service";
import { ClusterJewelTierService } from "../tiering/cluster-jewel-tier.service";
import { RuleId } from "../../models/rule-id";
import { Economy } from "../../models/economy-models";

@Injectable({
  providedIn: 'root'
})
export class JewelleryGeneratorService {
  constructor(
    private ruleFormatter: RuleGeneratorService,
    private helper: GenerationHelperService,
    private itemDataService: ItemDataService,
    private clusterJewelTierService: ClusterJewelTierService,
  ) {
  }

  public generateVeiledJewelleryHighlightRules(filter: Filter) {
    if (!filter.showVeiledJewellery) return '';

    let customLine = '';

    if (filter.showVeiledJewelleryOfSyndicateMembers && filter.showVeiledJewelleryOfSyndicateMembers.length > 0) {
      if (filter.showVeiledJewelleryOfSyndicateMembers.includes('All Members')) {
        customLine += `\n  HasExplicitMod >=1 ${this.itemDataService.getAllSyndicateMembers()
          .filter(m => m.name !== 'All Veiled' && m.name !== 'All Members')
          .map(m => `"${m.modName || m.name}"`).join(' ')}`;
      } else if (!filter.showVeiledJewelleryOfSyndicateMembers.includes('All Veiled')) {
        const syndicateMembersString = filter.showVeiledJewelleryOfSyndicateMembers.map((member: string) => `"${member}"`).join(' ');
        customLine += `\n  HasExplicitMod >=1 ${syndicateMembersString}`;
      }
    }

    const veiledJewelleryRule = this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewelleryVeiled, new CustomRule({
      filterItemClass: '"Amulets" "Rings" "Belts" "Jewels" "Abyss Jewels"',
      advancedConditions: new AdvancedConditions({
        veiled: true,
      }),
    }), filter, 'Veiled Jewellery', { customLine });

    return veiledJewelleryRule;
  }

  public generateSynthesisedJewelleryHighlightRules(filter: Filter) {
    if (!filter.showSynthesisedJewellery) return '';

    const synthesisedJewelleryRule = this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewellerySynthesised, new CustomRule({
      filterItemClass: '"Amulets" "Rings" "Belts" "Jewels" "Abyss Jewels"',
      advancedConditions: new AdvancedConditions({
        synthesised: true,
      }),
    }), filter, 'Synthesised Jewellery');

    return synthesisedJewelleryRule;
  }

  public generateFracturedJewelleryHighlightRules(filter: Filter) {
    if (!filter.showFracturedJewellery) return '';

    const fracturedJewelleryRule = this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewelleryFractured, new CustomRule({
      filterItemClass: '"Amulets" "Rings" "Belts" "Jewels" "Abyss Jewels"',
      advancedConditions: new AdvancedConditions({
        fractured: true,
      }),
    }), filter, 'Fractured Jewellery');

    return fracturedJewelleryRule;
  }

  public generateInfluencedJewelleryHighlightRules(filter: Filter) {
    if (!filter.showInfluencedJewellery) return '';

    const influencedJewelleryRule = this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewelleryInfluenced, new CustomRule({
      filterItemClass: '"Amulets" "Rings" "Belts" "Jewels" "Abyss Jewels"',
      advancedConditions: new AdvancedConditions({
        hasInfluence: true,
      }),
    }), filter, 'Influenced Jewellery');

    return influencedJewelleryRule;
  }

  public generateMemoryStrandJewelleryHighlightRules(filter: Filter) {
    if (!filter.showMemoryStrandJewellery) return '';

    const memoryStrandJewelleryRule = this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewelleryMemoryStrands, new CustomRule({
      filterItemClass: '"Amulets" "Rings" "Belts" "Jewels" "Abyss Jewels"',
      advancedConditions: new AdvancedConditions({
        memoryStrands: filter.showMemoryStrandJewelleryMinStrands ?? 1,
      }),
    }), filter, 'Memory Strand Jewellery');

    return memoryStrandJewelleryRule;
  }

  public generateAnointedAmuletsHighlightRules(filter: Filter) {
    if (!filter.showAnointedAmulets) return '';

    const anointedAmuletsRule = this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewelleryAnointedAmulets, new CustomRule({
      itemClass: 'Amulets',
      advancedConditions: new AdvancedConditions({ anyEnchantment: true, corrupted: YesNoAny.No }),
    }), filter);

    return anointedAmuletsRule;
  }

  public generateCorruptedImplicitJewelsHighlightRules(filter: Filter) {
    if (!filter.showCorruptedImplicitJewels) return '';

    const rule = new CustomRule({
        advancedConditions: new AdvancedConditions({
          corrupted: YesNoAny.Yes,
          identified: YesNoAny.Yes,
        }),
    });

    const unidentifiedRule = new CustomRule({
        advancedConditions: new AdvancedConditions({
          corrupted: YesNoAny.Yes,
          identified: YesNoAny.No,
        }),
    });

    if (filter.showCorruptedImplicitJewelsMinImplicits > 0) {
      rule.advancedConditions!.corruptedImplicits = filter.showCorruptedImplicitJewelsMinImplicits;
      unidentifiedRule.advancedConditions!.corruptedImplicits = filter.showCorruptedImplicitJewelsMinImplicits;
    }

    if (filter.showCorruptedImplicitJewelsMinExplicits && filter.showCorruptedImplicitJewelsMinExplicits > 0) {
      rule.advancedConditions!.hasExplicitMod = '"a" "e" "i" "o" "u" "y"';
      rule.advancedConditions!.hasExplicitModMin = filter.showCorruptedImplicitJewelsMinExplicits;
    }

    if (filter.showCorruptedImplicitJewelsTypes.length > 0 && !filter.showCorruptedImplicitJewelsTypes.includes('All')) {
      rule.baseTypes = filter.showCorruptedImplicitJewelsTypes;
      unidentifiedRule.baseTypes = filter.showCorruptedImplicitJewelsTypes;
      const hasAbyssJewels = filter.showCorruptedImplicitJewelsTypes.some((t: string) => t.includes('Eye'));
      const hasRegularJewels = filter.showCorruptedImplicitJewelsTypes.some((t: string) => !t.includes('Eye'));
      rule.filterItemClass = `${hasAbyssJewels ? '"Abyss Jewels"' : ''}${hasRegularJewels ? ' "Jewels"' : ''}`;
      unidentifiedRule.filterItemClass = `${hasAbyssJewels ? '"Abyss Jewels"' : ''}${hasRegularJewels ? ' "Jewels"' : ''}`;
    } else {
      rule.filterItemClass = '"Jewels" "Abyss Jewels"';
      unidentifiedRule.filterItemClass = '"Jewels" "Abyss Jewels"';
    }

    return this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewelleryCorruptedJewels, rule, filter, 'Identified Corrupted Implicit Jewels')
      + '\n\n' + this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewelleryCorruptedJewels, unidentifiedRule, filter, 'Unidentified Corrupted Implicit Jewels');
  }

  public generateCorruptedImplicitJewelleryHighlightRules(filter: Filter) {
    if (!filter.showCorruptedImplicitJewellery) return '';

    const selectedClasses = filter.showCorruptedImplicitJewelleryClasses;
    const allClasses = '"Amulets" "Rings" "Belts"';
    const filterItemClass = (selectedClasses.length === 0 || selectedClasses.includes('All'))
      ? allClasses
      : selectedClasses.map(c => `"${c}"`).join(' ');

    const rule = new CustomRule({
      rarity: filter.showCorruptedImplicitJewelleryMinRarity,
      filterItemClass,
      advancedConditions: new AdvancedConditions({
        corrupted: YesNoAny.Yes,
        identified: YesNoAny.Yes,
        corruptedImplicits: filter.showCorruptedImplicitJewelleryMinImplicits > 0
          ? filter.showCorruptedImplicitJewelleryMinImplicits
          : undefined,
      }),
    });

    return this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewelleryCorruptedJewellery, rule, filter);
  }

  public generateClusterJewelHighlightRules(filter: Filter) {
    if (filter.economy === Economy.Fixed) {
      return '';
    }

    const minLevel = filter.showClusterJewelBasesMinItemLevel ?? 1;

    const excellentClusterJewelBasesRules = !filter.showExcellentClusterJewelBases
      ? ''
      : this.clusterJewelTierService.getExcellentClusterJewelBases()
        .filter(jewel => (jewel.minItemLevel ?? 0) >= minLevel)
        .map((jewel) => {
        return this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewelleryExcellentClusterJewelBases, new CustomRule({
          itemClass: 'Jewels',
          baseTypes: [jewel.baseType],
          advancedConditions: new AdvancedConditions({
            minItemLevel: jewel.minItemLevel,
            maxItemLevel: jewel.maxItemLevel,
            minClusterJewelPassives: jewel.passiveNum,
            maxClusterJewelPassives: jewel.passiveNum,
            clusterJewelPassiveNodes: jewel.passiveNodes,
          }),
          startWithClass: true,
        }), filter, `Excellent ${jewel.baseType}s ilvl ${jewel.minItemLevel}${jewel.maxItemLevel ? ` - ${jewel.maxItemLevel}` : '+'}`);
      }).join('\n\n');

    const goodClusterJewelBasesRules = !filter.showGoodClusterJewelBases
      ? ''
      : this.clusterJewelTierService.getGoodClusterJewelBases()
        .filter(jewel => (jewel.minItemLevel ?? 0) >= minLevel)
        .map((jewel) => {
        return this.ruleFormatter.generateEquipmentRule(EquipmentType.OtherJewelleryGoodClusterJewelBases, new CustomRule({
          itemClass: 'Jewels',
          baseTypes: [jewel.baseType],
          advancedConditions: new AdvancedConditions({
            minItemLevel: jewel.minItemLevel,
            maxItemLevel: jewel.maxItemLevel,
            minClusterJewelPassives: jewel.passiveNum,
            maxClusterJewelPassives: jewel.passiveNum,
            clusterJewelPassiveNodes: jewel.passiveNodes,
          }),
          startWithClass: true,
        }), filter, `Good ${jewel.baseType}s ilvl ${jewel.minItemLevel}${jewel.maxItemLevel ? ` - ${jewel.maxItemLevel}` : '+'}`);
      }).join('\n\n');

    return excellentClusterJewelBasesRules + '\n\n' + goodClusterJewelBasesRules;
  }

  public generateMyJewelleryRules(filter: Filter): { highlightRules: string, showRules: string } {
    let highlightRules = '';
    let showRules = '';

    if (!filter.jewelleryFilters || filter.jewelleryFilters.length === 0) {
      return { highlightRules, showRules };
    }

    // Group jewelleryFilters by jewelleryClass
    const groupedByClass: { [key: string]: JewelleryFilter[] } = {};
    filter.jewelleryFilters
      .filter(j => j.show)
      .forEach(jewellery => {
        const classKey = jewellery.jewelleryType.toString();
        if (!groupedByClass[classKey]) {
          groupedByClass[classKey] = [];
        }
        groupedByClass[classKey].push(jewellery);
      });

    const classOrder = ['Amulets', 'Rings', 'Belts', 'Talismans'];

    for (const jewelleryClass of classOrder) {
      if (!groupedByClass[jewelleryClass]) continue;

      const jewelleries = groupedByClass[jewelleryClass];

      jewelleries.forEach(jewellery => {
        const originalIndex = filter.jewelleryFilters.findIndex(j => j === jewellery);
        const baseTypes = (!jewellery.jewelleryBaseTypes || jewellery.jewelleryBaseTypes.includes('All')) ? ['All'] : jewellery.jewelleryBaseTypes;
        const isPoe1Talisman = poe1 && jewellery.jewelleryType === JewelleryType.Talismans;
        const itemClassForRule = isPoe1Talisman ? 'Amulets' : jewellery.jewelleryType.toString();
        const talismanAllBaseTypesOptions: Partial<FormatRuleOptions> = isPoe1Talisman && baseTypes.includes('All')
          ? { customBaseTypes: '"Talisman"' }
          : {};
        const classes = `"${jewellery.jewelleryType}"`;

        if (poe2) {
          const jewelleryTypeName = jewellery.jewelleryType.toString();
          if (jewellery.highlightConfig.rare) {
            const rareT5Tier = this.helper.getEquipmentTier(EquipmentType.MyJewelleryRareT5);
            const rareT4Tier = this.helper.getEquipmentTier(EquipmentType.MyJewelleryRareT4);
            const rareT3Tier = this.helper.getEquipmentTier(EquipmentType.MyJewelleryRareT3);
            const rareT2Tier = this.helper.getEquipmentTier(EquipmentType.MyJewelleryRareT2);

            highlightRules += (rareT5Tier === rareT4Tier && jewellery.highlightConfig.rareMinTier < 5) ? '' : this.formatHighlightForTieredJewellery(Rarity.Rare, EquipmentType.MyJewelleryRareT5, jewellery.highlightConfig.rareMinTier, filter, baseTypes, classes, 5, jewellery, 'jewellery-my-rare-t5', `Favourite T5 Rare ${jewelleryTypeName}`, 'jewellery', originalIndex);
            highlightRules += (rareT4Tier === rareT3Tier && jewellery.highlightConfig.rareMinTier < 4) ? '' : this.formatHighlightForTieredJewellery(Rarity.Rare, EquipmentType.MyJewelleryRareT4, jewellery.highlightConfig.rareMinTier, filter, baseTypes, classes, 4, jewellery, 'jewellery-my-rare-t4', `Favourite T4 Rare ${jewelleryTypeName}`, 'jewellery', originalIndex);
            highlightRules += (rareT3Tier === rareT2Tier && jewellery.highlightConfig.rareMinTier < 3) ? '' : this.formatHighlightForTieredJewellery(Rarity.Rare, EquipmentType.MyJewelleryRareT3, jewellery.highlightConfig.rareMinTier, filter, baseTypes, classes, 3, jewellery, 'jewellery-my-rare-t3', `Favourite T3 Rare ${jewelleryTypeName}`, 'jewellery', originalIndex);
            highlightRules += this.formatHighlightForTieredJewellery(Rarity.Rare, EquipmentType.MyJewelleryRareT2, jewellery.highlightConfig.rareMinTier, filter, baseTypes, classes, 2, jewellery, 'jewellery-my-rare-t2', `Favourite T2 Rare ${jewelleryTypeName}`, 'jewellery', originalIndex);
          }

          if (jewellery.highlightConfig.magic) {
            const magicT5Tier = this.helper.getEquipmentTier(EquipmentType.MyJewelleryMagicT5);
            const magicT4Tier = this.helper.getEquipmentTier(EquipmentType.MyJewelleryMagicT4);
            const magicT3Tier = this.helper.getEquipmentTier(EquipmentType.MyJewelleryMagicT3);
            const magicT2Tier = this.helper.getEquipmentTier(EquipmentType.MyJewelleryMagicT2);

            highlightRules += (magicT5Tier === magicT4Tier && jewellery.highlightConfig.magicMinTier < 5) ? '' : this.formatHighlightForTieredJewellery(Rarity.Magic, EquipmentType.MyJewelleryMagicT5, jewellery.highlightConfig.magicMinTier, filter, baseTypes, classes, 5, jewellery, 'jewellery-my-magic-t5', `Favourite T5 Magic ${jewelleryTypeName}`, 'jewellery', originalIndex);
            highlightRules += (magicT4Tier === magicT3Tier && jewellery.highlightConfig.magicMinTier < 4) ? '' : this.formatHighlightForTieredJewellery(Rarity.Magic, EquipmentType.MyJewelleryMagicT4, jewellery.highlightConfig.magicMinTier, filter, baseTypes, classes, 4, jewellery, 'jewellery-my-magic-t4', `Favourite T4 Magic ${jewelleryTypeName}`, 'jewellery', originalIndex);
            highlightRules += (magicT3Tier === magicT2Tier && jewellery.highlightConfig.magicMinTier < 3) ? '' : this.formatHighlightForTieredJewellery(Rarity.Magic, EquipmentType.MyJewelleryMagicT3, jewellery.highlightConfig.magicMinTier, filter, baseTypes, classes, 3, jewellery, 'jewellery-my-magic-t3', `Favourite T3 Magic ${jewelleryTypeName}`, 'jewellery', originalIndex);
            highlightRules += this.formatHighlightForTieredJewellery(Rarity.Magic, EquipmentType.MyJewelleryMagicT2, jewellery.highlightConfig.magicMinTier, filter, baseTypes, classes, 2, jewellery, 'jewellery-my-magic-t2', `Favourite T2 Magic ${jewelleryTypeName}`, 'jewellery', originalIndex);
          }

          if (jewellery.highlightConfig.excellentILevel) {
            const jewelleryTypeName = jewellery.jewelleryType.toString();
            highlightRules += this.ruleFormatter.generateEquipmentRule(
              EquipmentType.MyJewelleryExcellentILevel,
              new CustomRule({
                itemClass: itemClassForRule,
                rarityComparator: Comparator.Equal,
                rarity: Rarity.Normal,
                advancedConditions: Object.assign(this.buildAdvancedConditions(jewellery), {
                  minItemLevel: jewellery.highlightConfig.excellentILevelMin ?? jewellery.itemLevel ?? 82,
                }),
                baseTypes: baseTypes,
              }),
              filter,
              `Favourite Excellent Item Level ${jewelleryTypeName}`,
              talismanAllBaseTypesOptions,
              undefined,
              undefined,
              'jewellery',
              originalIndex
            ) + '\n\n';
          }

          if (jewellery.highlightConfig.goodILevel) {
            const jewelleryTypeName = jewellery.jewelleryType.toString();
            highlightRules += this.ruleFormatter.generateEquipmentRule(
              EquipmentType.MyJewelleryGoodILevel,
              new CustomRule({
                itemClass: itemClassForRule,
                rarityComparator: Comparator.Equal,
                rarity: Rarity.Normal,
                advancedConditions: Object.assign(this.buildAdvancedConditions(jewellery), {
                  minItemLevel: jewellery.highlightConfig.goodILevelMin ?? jewellery.itemLevel ?? 81,
                }),
                baseTypes: baseTypes,
              }),
              filter,
              `Favourite Good Item Level ${jewelleryTypeName}`,
              talismanAllBaseTypesOptions,
              undefined,
              undefined,
              'jewellery',
              originalIndex
            ) + '\n\n';
          }
        }

        // TODO: investigate why this seems to get generated twice?

        if (poe1) {
          const jewelleryTypeName = jewellery.jewelleryType.toString();
          // POE1: Item Level (Excellent/Good)
          if (jewellery.highlightConfig.excellentILevel && (jewellery.itemLevel ?? 0) <= (jewellery.highlightConfig.excellentILevelMin ?? 85)) {
            highlightRules += this.ruleFormatter.generateEquipmentRule(
              EquipmentType.MyJewelleryExcellentILevel,
              new CustomRule({
                itemClass: itemClassForRule,
                advancedConditions: Object.assign(this.buildAdvancedConditions(jewellery), {
                  minItemLevel: jewellery.highlightConfig.excellentILevelMin ?? 85,
                }),
                baseTypes: baseTypes,
              }),
              filter,
              `Favourite Excellent Item Level ${jewelleryTypeName}`,
              talismanAllBaseTypesOptions,
              undefined,
              undefined,
              'jewellery',
              originalIndex
            ) + '\n\n';
          }

          if (jewellery.highlightConfig.goodILevel && (jewellery.itemLevel ?? 0) <= (jewellery.highlightConfig.goodILevelMin ?? 84)) {
            highlightRules += this.ruleFormatter.generateEquipmentRule(
              EquipmentType.MyJewelleryGoodILevel,
              new CustomRule({
                itemClass: itemClassForRule,
                advancedConditions: Object.assign(this.buildAdvancedConditions(jewellery), {
                  minItemLevel: jewellery.highlightConfig.goodILevelMin ?? 84,
                }),
                baseTypes: baseTypes,
              }),
              filter,
              `Favourite Good Item Level ${jewelleryTypeName}`,
              talismanAllBaseTypesOptions,
              undefined,
              undefined,
              'jewellery',
              originalIndex
            ) + '\n\n';
          }

          // POE1: Memory Strands (Excellent/Good)
          if (jewellery.highlightConfig.excellentMemoryStrands) {
            highlightRules += this.ruleFormatter.generateEquipmentRule(
              EquipmentType.MyJewelleryExcellentMemoryStrands,
              new CustomRule({
                itemClass: itemClassForRule,
                advancedConditions: Object.assign(this.buildAdvancedConditions(jewellery), {
                  memoryStrands: jewellery.highlightConfig.excellentMemoryStrandsMinStrands ?? 1,
                }),
                baseTypes: baseTypes,
              }),
              filter,
              `Favourite Excellent Memory Strands ${jewelleryTypeName}`,
              talismanAllBaseTypesOptions,
              undefined,
              undefined,
              'jewellery',
              originalIndex
            ) + '\n\n';
          }

          if (jewellery.highlightConfig.goodMemoryStrands) {
            highlightRules += this.ruleFormatter.generateEquipmentRule(
              EquipmentType.MyJewelleryGoodMemoryStrands,
              new CustomRule({
                itemClass: itemClassForRule,
                advancedConditions: Object.assign(this.buildAdvancedConditions(jewellery), {
                  memoryStrands: jewellery.highlightConfig.goodMemoryStrandsMinStrands ?? 1,
                }),
                baseTypes: baseTypes,
              }),
              filter,
              `Favourite Good Memory Strands ${jewelleryTypeName}`,
              talismanAllBaseTypesOptions,
              undefined,
              undefined,
              'jewellery',
              originalIndex
            ) + '\n\n';
          }
        }

        // Generate show rules for regularRare (if enabled)
        if (jewellery.highlightConfig.regularRare) {
          const jewelleryTypeName = jewellery.jewelleryType.toString();
          showRules += this.ruleFormatter.generateEquipmentRule(
            EquipmentType.MyJewelleryRegularRare,
            new CustomRule({
              itemClass: itemClassForRule,
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Rare,
              baseTypes: baseTypes,
              advancedConditions: Object.assign(this.buildAdvancedConditions(jewellery), {
                minItemLevel: jewellery.itemLevel,
                unidentifiedTier: jewellery.unidentifiedTier,
              }),
            }),
            filter,
            `Favourite Regular Rare ${jewelleryTypeName}`,
            talismanAllBaseTypesOptions,
            undefined,
            undefined,
            'jewellery',
            originalIndex
          ) + '\n\n';
        }

        // Generate show rules for regularMagic (if enabled and rarity is Magic or Normal)
        if (jewellery.highlightConfig.regularMagic && (jewellery.rarity === Rarity.Magic || jewellery.rarity === Rarity.Normal)) {
          const jewelleryTypeName = jewellery.jewelleryType.toString();
          showRules += this.ruleFormatter.generateEquipmentRule(
            EquipmentType.MyJewelleryRegularMagic,
            new CustomRule({
              itemClass: itemClassForRule,
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Magic,
              baseTypes: baseTypes,
              advancedConditions: Object.assign(this.buildAdvancedConditions(jewellery), {
                minItemLevel: jewellery.itemLevel,
                unidentifiedTier: jewellery.unidentifiedTier,
              }),
            }),
            filter,
            `Favourite Regular Magic ${jewelleryTypeName}`,
            talismanAllBaseTypesOptions,
            undefined,
            undefined,
            'jewellery',
            originalIndex
          ) + '\n\n';
        }

        // Generate show rules for regularNormal (if enabled and rarity is Normal)
        if (jewellery.highlightConfig.regularNormal && jewellery.rarity === Rarity.Normal) {
          const jewelleryTypeName = jewellery.jewelleryType.toString();
          showRules += this.ruleFormatter.generateEquipmentRule(
            EquipmentType.MyJewelleryRegularNormal,
            new CustomRule({
              itemClass: itemClassForRule,
              rarityComparator: Comparator.Equal,
              rarity: Rarity.Normal,
              baseTypes: baseTypes,
              advancedConditions: Object.assign(this.buildAdvancedConditions(jewellery), {
                minItemLevel: jewellery.itemLevel,
                unidentifiedTier: jewellery.unidentifiedTier,
              }),
            }),
            filter,
            `Favourite Regular Normal ${jewelleryTypeName}`,
            talismanAllBaseTypesOptions,
            undefined,
            undefined,
            'jewellery',
            originalIndex
          ) + '\n\n';
        }
      });
    }

    return { highlightRules: highlightRules.trim(), showRules: showRules.trim() };
  }

  public generateMyJewelsRules(filter: Filter): { highlightRules: string, showRules: string } {
    let highlightRules = '';
    let showRules = '';

    if (!filter.jewelFilters || filter.jewelFilters.length === 0) {
      return { highlightRules, showRules };
    }

    filter.jewelFilters.filter(j => j.show).forEach((jewel, jewelIndex) => {
      const baseTypes = jewel.jewelType;
      const classes = poe1 ? '"Jewels" "Abyss Jewels"' : '"Jewels"';

      if (poe1) {
        const abyssJewelTypes: JewelType[] = [
          JewelType.MurderousEye,
          JewelType.SearchingEye,
          JewelType.HypnoticEye,
          JewelType.GhastlyEye,
        ];
        const hasSelectedAbyssJewelBases =
          baseTypes.includes(JewelType.All) ||
          abyssJewelTypes.some(at => baseTypes.includes(at));

        // POE1: Item Level (Excellent/Good) -- only for Abyss jewels in jewelType
        if (hasSelectedAbyssJewelBases && jewel.highlightConfig.excellentILevel && (jewel.itemLevel ?? 0) <= (jewel.highlightConfig.excellentILevelMin ?? 85)) {
          highlightRules += this.ruleFormatter.generateEquipmentRule(
            EquipmentType.MyJewelsExcellentILevel,
            new CustomRule({
              itemClass: 'Abyss Jewels',
              advancedConditions: Object.assign(this.buildAdvancedConditions(jewel), {
                minItemLevel: jewel.highlightConfig.excellentILevelMin ?? 85,
              }),
              baseTypes: baseTypes.filter(t => abyssJewelTypes.includes(t as JewelType)),
            }),
            filter,
            'Favourite Excellent Item Level Abyss Jewels',
            undefined,
            undefined,
            undefined,
            'jewel',
            jewelIndex
          ) + '\n\n';
        }

        if (hasSelectedAbyssJewelBases && jewel.highlightConfig.goodILevel && (jewel.itemLevel ?? 0) <= (jewel.highlightConfig.goodILevelMin ?? 84)) {
          highlightRules += this.ruleFormatter.generateEquipmentRule(
            EquipmentType.MyJewelsGoodILevel,
            new CustomRule({
              itemClass: 'Abyss Jewels',
              advancedConditions: Object.assign(this.buildAdvancedConditions(jewel), {
                minItemLevel: jewel.highlightConfig.goodILevelMin ?? 84,
              }),
              baseTypes: baseTypes.filter(t => abyssJewelTypes.includes(t as JewelType)),
            }),
            filter,
            'Favourite Good Item Level Abyss Jewels',
            undefined,
            undefined,
            undefined,
            'jewel',
            jewelIndex
          ) + '\n\n';
        }
      }

      // Generate show rules for regularRare (if enabled)
      if (jewel.highlightConfig.regularRare) {
        showRules += this.ruleFormatter.generateEquipmentRule(
          EquipmentType.MyJewelsRare,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Rare,
            baseTypes: baseTypes,
            advancedConditions: Object.assign(this.buildAdvancedConditions(jewel), {
              minItemLevel: jewel.itemLevel,
            }),
          }),
          filter,
          'Favourite Regular Rare Jewels',
          { customClass: classes },
          undefined,
          undefined,
          'jewel',
          jewelIndex
        ) + '\n\n';
      }

      // Generate show rules for regularMagic (if enabled and rarity is Magic or Normal)
      if (jewel.highlightConfig.regularMagic && jewel.rarity === Rarity.Magic) {
        showRules += this.ruleFormatter.generateEquipmentRule(
          EquipmentType.MyJewelsMagic,
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Magic,
            baseTypes: baseTypes,
            advancedConditions: Object.assign(this.buildAdvancedConditions(jewel), {
              minItemLevel: jewel.itemLevel,
            }),
          }),
          filter,
          'Favourite Regular Magic Jewels',
          { customClass: classes },
          undefined,
          undefined,
          'jewel',
          jewelIndex
        ) + '\n\n';
      }
    });

    return { highlightRules: highlightRules.trim(), showRules: showRules.trim() };
  }

  public generateUnidentifiedJewelleryRules(filter: Filter): string {
    if (poe1) return '';

    let rareUnidentifiedJewelleryRules = '';

    if (filter.showTieredRareJewellery) {
      const classes = filter.showTieredRareJewelleryTypes?.includes(JewelleryType.All) ? '"Amulets" "Rings" "Belts"' : filter.showTieredRareJewelleryTypes.map(t => `"${t}"`).join(' ');

      const rareT5Tier = this.helper.getEquipmentTier(EquipmentType.OtherJewelleryRareT5);
      const rareT4Tier = this.helper.getEquipmentTier(EquipmentType.OtherJewelleryRareT4);
      const rareT3Tier = this.helper.getEquipmentTier(EquipmentType.OtherJewelleryRareT3);
      const rareT2Tier = this.helper.getEquipmentTier(EquipmentType.OtherJewelleryRareT2);

      rareUnidentifiedJewelleryRules += (rareT5Tier === rareT4Tier && filter.showTieredRareJewelleryMinTier < 5) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherJewelleryRareT5, filter.showTieredRareJewelleryMinTier, filter, ['All'], classes, 5, undefined, undefined, undefined, undefined, 'jewellery-unidentified-tier-rare-t5', 'T5 Rare Unidentified Jewellery');
      rareUnidentifiedJewelleryRules += (rareT4Tier === rareT3Tier && filter.showTieredRareJewelleryMinTier < 4) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherJewelleryRareT4, filter.showTieredRareJewelleryMinTier, filter, ['All'], classes, 4, undefined, undefined, undefined, undefined, 'jewellery-unidentified-tier-rare-t4', 'T4 Rare Unidentified Jewellery');
      rareUnidentifiedJewelleryRules += (rareT3Tier === rareT2Tier && filter.showTieredRareJewelleryMinTier < 3) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherJewelleryRareT3, filter.showTieredRareJewelleryMinTier, filter, ['All'], classes, 3, undefined, undefined, undefined, undefined, 'jewellery-unidentified-tier-rare-t3', 'T3 Rare Unidentified Jewellery');
      rareUnidentifiedJewelleryRules += this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Rare, EquipmentType.OtherJewelleryRareT2, filter.showTieredRareJewelleryMinTier, filter, ['All'], classes, 2, undefined, undefined, undefined, undefined, 'jewellery-unidentified-tier-rare-t2', 'T2 Rare Unidentified Jewellery');
    }

    let magicUnidentifiedJewelleryRules = '';

    if (filter.showTieredMagicJewellery) {
      const classes = filter.showTieredMagicJewelleryTypes?.includes(JewelleryType.All) ? '"Amulets" "Rings" "Belts"' : filter.showTieredMagicJewelleryTypes.map(t => `"${t}"`).join(' ');

      const magicT5Tier = this.helper.getEquipmentTier(EquipmentType.OtherJewelleryMagicT5);
      const magicT4Tier = this.helper.getEquipmentTier(EquipmentType.OtherJewelleryMagicT4);
      const magicT3Tier = this.helper.getEquipmentTier(EquipmentType.OtherJewelleryMagicT3);
      const magicT2Tier = this.helper.getEquipmentTier(EquipmentType.OtherJewelleryMagicT2);

      magicUnidentifiedJewelleryRules += (magicT5Tier === magicT4Tier && filter.showTieredMagicJewelleryMinTier < 5) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherJewelleryMagicT5, filter.showTieredMagicJewelleryMinTier, filter, ['All'], classes, 5, undefined, undefined, undefined, undefined, 'jewellery-unidentified-tier-magic-t5', 'T5 Magic Unidentified Jewellery');
      magicUnidentifiedJewelleryRules += (magicT4Tier === magicT3Tier && filter.showTieredMagicJewelleryMinTier < 4) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherJewelleryMagicT4, filter.showTieredMagicJewelleryMinTier, filter, ['All'], classes, 4, undefined, undefined, undefined, undefined, 'jewellery-unidentified-tier-magic-t4', 'T4 Magic Unidentified Jewellery');
      magicUnidentifiedJewelleryRules += (magicT3Tier === magicT2Tier && filter.showTieredMagicJewelleryMinTier < 3) ? '' : this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherJewelleryMagicT3, filter.showTieredMagicJewelleryMinTier, filter, ['All'], classes, 3, undefined, undefined, undefined, undefined, 'jewellery-unidentified-tier-magic-t3', 'T3 Magic Unidentified Jewellery');
      magicUnidentifiedJewelleryRules += this.ruleFormatter.formatHighlightForTieredEquipment(Rarity.Magic, EquipmentType.OtherJewelleryMagicT2, filter.showTieredMagicJewelleryMinTier, filter, ['All'], classes, 2, undefined, undefined, undefined, undefined, 'jewellery-unidentified-tier-magic-t2', 'T2 Magic Unidentified Jewellery');
    }

    return rareUnidentifiedJewelleryRules + '\n\n' + magicUnidentifiedJewelleryRules;
  }

  private formatHighlightForTieredJewellery(rarity: Rarity, equipmentType: EquipmentType, minTier: number, filter: Filter, baseTypes: string[], classes: string, tier: number, jewellery: JewelleryFilter, id?: RuleId, name?: string, myEquipmentType?: 'weapon' | 'armour' | 'jewellery' | 'jewel' | 'flask', myEquipmentIndex?: number) {
    if (minTier <= tier) {
      return this.ruleFormatter.generateEquipmentRule(
        equipmentType,
        new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: rarity,
          advancedConditions: Object.assign(this.buildAdvancedConditions(jewellery), {
            minItemLevel: jewellery.itemLevel,
            unidentifiedTier: tier,
          }) as AdvancedConditions,
          baseTypes: baseTypes,
        }),
        filter,
        name,
        { customClass: classes },
        id,
        undefined,
        myEquipmentType,
        myEquipmentIndex
      ) + '\n\n';
    }

    return '';
  }

  private buildAdvancedConditions(jewellery: JewelleryFilter | JewelFilter): AdvancedConditions {
    const advancedConditions = new AdvancedConditions();

    advancedConditions.corrupted = jewellery.corrupted;
    advancedConditions.corruptedImplicits = jewellery.corruptedImplicits;
    advancedConditions.fractured = jewellery.fractured;
    advancedConditions.synthesised = jewellery.synthesised;
    advancedConditions.veiled = jewellery.veiled;
    advancedConditions.hasInfluence = jewellery.hasInfluence;
    advancedConditions.influences = jewellery.influences;
    advancedConditions.memoryStrands = jewellery.minMemoryStrands;
    advancedConditions.minQuality = jewellery.minQuality;
    advancedConditions.minItemLevel = jewellery.itemLevel;

    return advancedConditions;
  }
}

